import { useState, useEffect, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { booksService } from '../api/booksService';
import type { BookUploadDto, BookUploadStatusDto } from '../types/book';
import { BookFormat } from '../types/book';

/**
 * Hook for uploading book files (EPUB or PDF) with optional cover image
 * Automatically detects format from file extension and calls the appropriate upload method
 * @deprecated Use useAsyncUploadBook for better UX with background processing
 */
export function useUploadBook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, file, coverImage }: { data: BookUploadDto; file: File; coverImage?: File }) => {
      // Detect format from file extension
      const fileExtension = file.name.split('.').pop()?.toLowerCase();

      if (fileExtension === 'pdf') {
        // Upload as PDF
        return booksService.uploadPdf({ ...data, format: BookFormat.Pdf }, file, coverImage);
      } else {
        // Upload as EPUB (default)
        return booksService.uploadEpub({ ...data, format: BookFormat.Epub }, file, coverImage);
      }
    },
    onSuccess: () => {
      // Invalidate any book lists
      queryClient.invalidateQueries({ queryKey: ['books'] });
    },
  });
}

/**
 * Hook for async book upload with automatic status polling
 * Returns file upload to blob, then polls for background processing completion
 * Provides real-time status updates: Queued → Processing → Success/Failed
 */
export function useAsyncUploadBook() {
  const queryClient = useQueryClient();
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [trackingId, setTrackingId] = useState<string | null>(null);
  const [pollingStatus, setPollingStatus] = useState<BookUploadStatusDto | null>(null);
  const [pollingError, setPollingError] = useState<Error | null>(null);

  // Upload mutation (file upload only)
  const uploadMutation = useMutation({
    mutationFn: async ({ data, file, coverImage }: { data: BookUploadDto; file: File; coverImage?: File }) => {
      // Detect format from file extension
      const fileExtension = file.name.split('.').pop()?.toLowerCase();

      if (fileExtension === 'pdf') {
        // Upload as PDF
        return booksService.uploadPdfAsync(
          { ...data, format: BookFormat.Pdf },
          file,
          coverImage,
          setUploadProgress
        );
      } else {
        // Upload as EPUB (default)
        return booksService.uploadEpubAsync(
          { ...data, format: BookFormat.Epub },
          file,
          coverImage,
          setUploadProgress
        );
      }
    },
    onSuccess: (result) => {
      setTrackingId(result.trackingId);
      setUploadProgress(100); // File upload complete
    },
    onError: () => {
      setUploadProgress(0);
      setTrackingId(null);
    },
  });

  // Poll for processing status
  useEffect(() => {
    if (!trackingId) return;

    const pollInterval = setInterval(async () => {
      try {
        const status = await booksService.getUploadStatus(trackingId);
        setPollingStatus(status);

        // Stop polling when complete (success or failed)
        if (status.status === 'Success' || status.status === 'Failed') {
          clearInterval(pollInterval);

          // Invalidate book lists on success
          if (status.status === 'Success') {
            queryClient.invalidateQueries({ queryKey: ['books'] });
          }
        }
      } catch (error) {
        setPollingError(error as Error);
        clearInterval(pollInterval);
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [trackingId, queryClient]);

  // Reset function
  const reset = useCallback(() => {
    setUploadProgress(0);
    setTrackingId(null);
    setPollingStatus(null);
    setPollingError(null);
    uploadMutation.reset();
  }, [uploadMutation]);

  return {
    upload: uploadMutation.mutate,
    uploadAsync: uploadMutation.mutateAsync,
    uploadProgress,
    trackingId,
    status: pollingStatus,
    isUploading: uploadMutation.isPending,
    isProcessing: trackingId !== null && pollingStatus?.status !== 'Success' && pollingStatus?.status !== 'Failed',
    isComplete: pollingStatus?.status === 'Success',
    isFailed: pollingStatus?.status === 'Failed' || uploadMutation.isError || pollingError !== null,
    error: uploadMutation.error || pollingError || (pollingStatus?.status === 'Failed' ? new Error(pollingStatus.errorMessage || 'Processing failed') : null),
    bookId: pollingStatus?.bookId,
    reset,
  };
}

/**
 * Hook for fetching book metadata
 */
export function useBookMetadata(bookId: string | undefined) {
  return useQuery({
    queryKey: ['book-metadata', bookId],
    queryFn: () => booksService.getBookMetadata(bookId!),
    enabled: !!bookId,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes (prevents duplicate fetches)
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
}

/**
 * Hook for fetching chapter content
 */
export function useChapterContent(bookId: string, chapterIndex: number) {
  return useQuery({
    queryKey: ['chapter-content', bookId, chapterIndex],
    queryFn: () => booksService.getChapterContent({ bookId, chapterIndex }),
    enabled: !!bookId && chapterIndex >= 0,
  });
}
